
import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, RefreshCw, Globe, EyeOff, AlertTriangle } from "lucide-react";
import { useExpertStatus } from "@/hooks/useExpertStatus";

interface ExpertPublishingStatusProps {
  applicationId: string;
}

export default function ExpertPublishingStatus({ applicationId }: ExpertPublishingStatusProps) {
  const { expertStatus, loading, handleToggleExpertStatus, refreshStatus } = useExpertStatus(applicationId, true);
  const [refreshing, setRefreshing] = React.useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    await refreshStatus();
    setRefreshing(false);
  };

  const handleToggle = async () => {
    try {
      await handleToggleExpertStatus();
      // Refresh after a short delay to ensure the status is updated
      setTimeout(() => {
        handleRefresh();
      }, 1000);
    } catch (error) {
      console.error("Error toggling status:", error);
    }
  };

  if (loading) {
    return <div className="w-4 h-4 rounded-full bg-gray-200 animate-pulse"></div>;
  }

  if (!expertStatus) {
    return (
      <div className="flex items-center gap-1">
        <Badge variant="outline" className="text-xs">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Not Created
        </Badge>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing}
          className="h-6 w-6 p-0"
        >
          <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <Badge 
        variant={expertStatus === 'active' ? 'default' : 'secondary'}
        className={`text-xs cursor-pointer transition-colors ${
          expertStatus === 'active' 
            ? 'bg-green-600 hover:bg-green-700 text-white' 
            : 'bg-gray-500 hover:bg-gray-600 text-white'
        }`}
        onClick={handleToggle}
      >
        {expertStatus === 'active' ? (
          <>
            <Globe className="h-3 w-3 mr-1" />
            Published
          </>
        ) : (
          <>
            <EyeOff className="h-3 w-3 mr-1" />
            Unpublished
          </>
        )}
      </Badge>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleRefresh}
        disabled={refreshing}
        className="h-6 w-6 p-0"
        title="Refresh status"
      >
        <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
      </Button>
    </div>
  );
}
